import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { sueadicApi, pagadoresApi } from '../api/rrhh'

// Formato US (Presea + Excel del usuario): 1,234,567.89 — coma miles, punto decimal.
const fmt = (n) => (n == null || n === '' ? '' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

// Parser tolerante: acepta "130,000.00" (US), "130.000,00" (AR) y "130000".
// Detecta el último separador como decimal. Devuelve number o NaN.
const parseNum = (s) => {
  if (s == null || s === '') return 0
  let str = String(s).replace(/[\s$]/g, '')
  if (str === '') return 0
  const lastDot = str.lastIndexOf('.')
  const lastComma = str.lastIndexOf(',')
  if (lastDot === -1 && lastComma === -1) return Number(str) || 0
  if (lastDot > lastComma) str = str.replace(/,/g, '')                            // US
  else                     str = str.replace(/\./g, '').replace(',', '.')         // AR
  const n = Number(str)
  return Number.isFinite(n) ? n : 0
}

// Pide tipear una palabra exacta para confirmar una acción destructiva.
const confirmType = (mensaje, palabra = 'eliminar') => {
  const v = window.prompt(`${mensaje}\n\nEscribí "${palabra}" para confirmar:`)
  return v != null && v.trim().toLowerCase() === palabra.toLowerCase()
}
const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

// --- cálculo en vivo (espejo de SueadicCalcService del backend) ---
const roundNear = (x, m) => Math.round(x / m) * m
// f.dto se guarda en el state como PORCENTAJE (5 = 5%), tal cual lo tipea
// el usuario. Solo dividimos por 100 al calcular o guardar.
function calcFila(f, netosMap) {
  // Banco PROPIO (del legajo del agente — lo que el agente cobró del banco).
  const infoPropio = netosMap[String(f.legajo)]
  const bancoPropio = infoPropio ? Number(infoPropio.neto) : 0

  // Banco para CÁLCULO: si hay legajo_referencia (paritarias), usa el banco
  // de ese legajo; si no, el propio. Refleja lo que hace el backend.
  const tieneRef = !!(f.legajo_referencia && String(f.legajo_referencia).trim() !== '')
  const infoRef = tieneRef ? netosMap[String(f.legajo_referencia).trim()] : null
  const bancoCalc = tieneRef ? (infoRef ? Number(infoRef.neto) : 0) : bancoPropio
  const tieneBanco = tieneRef ? !!infoRef : !!infoPropio

  const coef = Number(f.coeficiente) || 0
  const adelanto = Number(f.adelanto) || 0
  const adelantoCash = Number(f.adelanto_cash) || 0
  const dtoPct = Number(f.dto) || 0
  const dto = dtoPct / 100

  // Si hay ref, NO se suma el adelanto al banco_calc (el adelanto pertenece
  // al banco propio del agente, que es 0 cuando se usa ref).
  const adelantoCalc = tieneRef ? 0 : adelanto
  const totalBancoCalc = bancoCalc + adelantoCalc

  const manualNum = (f.acuerdo_manual !== '' && f.acuerdo_manual != null) ? Number(f.acuerdo_manual) : null
  const tieneManual = manualNum != null && !Number.isNaN(manualNum)
  const acuerdo = tieneManual ? manualNum : totalBancoCalc * coef
  const enMano = acuerdo * (1 - dto) - adelantoCash
  const totalGeneral = roundNear(enMano, 100)

  // Visible: el banco/total_banco mostrados son los del PROPIO agente. Si tiene
  // ref, ambos quedan en 0 (no se duplica visualmente el banco del referenciado).
  const totalBancoVisible = bancoPropio + adelanto
  return {
    banco: bancoPropio,
    total_banco: totalBancoVisible,
    acuerdo,
    tiene_acuerdo_manual: tieneManual,
    en_mano: enMano,
    total_general: totalGeneral,
    falta_banco: !tieneBanco && !!(f.legajo || f.legajo_referencia),
  }
}

const filaVacia = () => ({
  acuerdo_id: null, legajo: '', legajo_referencia: '', nombre: '', pagador_id: '',
  coeficiente: '', adelanto: '', adelanto_cash: '', dto: '',
  acuerdo_manual: '',
})

// El backend devuelve dto como fracción (0.05). El state lo guarda como
// porcentaje (5) para que coincida con lo que ve/escribe el usuario.
// Coeficiente: el backend lo guarda con 6 decimales (3.425900); limpiamos
// los ceros trailing una sola vez al cargar para mostrarlo prolijo (3.4259).
const filaDelBackend = (f) => ({
  ...f,
  dto: f.dto ? Number((Number(f.dto) * 100).toFixed(4)) : '',
  coeficiente: f.coeficiente == null || f.coeficiente === ''
    ? ''
    : String(f.coeficiente).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''),
})

export default function SueAdicPage() {
  const [periodos, setPeriodos] = useState([])
  const [periodoId, setPeriodoId] = useState(null)
  const [pagadores, setPagadores] = useState([])
  const [filas, setFilas] = useState([])
  const [netosMap, setNetosMap] = useState({})
  const [meta, setMeta] = useState(null)        // { estado, etiqueta, netos_sin_acuerdo, stats }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [showNetos, setShowNetos] = useState(false)
  const [netosTexto, setNetosTexto] = useState('')
  const [showImportar, setShowImportar] = useState(false)
  const [importarTexto, setImportarTexto] = useState('')
  const [showAvanzado, setShowAvanzado] = useState(false)
  // Filas con el sub-row de adelantos expandido (Set<number>).
  const [adelExpand, setAdelExpand] = useState(() => new Set())
  // Adicionales por pagador: { [pagador_id]: [{ concepto, importe, _key }, ...] }
  const [adicionales, setAdicionales] = useState({})
  // Compensaciones de terceros: [{ legajo, paga, _key }]
  const [compensaciones, setCompensaciones] = useState([])
  const [nuevoMes, setNuevoMes] = useState({ mes: new Date().getMonth() + 1, anio: new Date().getFullYear() })

  const cerrado = meta?.estado === 'cerrado'

  const cargarPeriodos = useCallback(async () => {
    const { data } = await sueadicApi.periodos()
    setPeriodos(data)
    return data
  }, [])

  const cargarTablero = useCallback(async (id) => {
    setLoading(true); setMsg('')
    try {
      const { data } = await sueadicApi.tablero(id)
      setFilas(data.filas.map(filaDelBackend))
      setNetosMap(data.netos_map || {})
      setPagadores(data.pagadores || [])
      setMeta({
        estado: data.periodo.estado,
        etiqueta: data.periodo.etiqueta,
        mes_trabajado: data.periodo.etiqueta_mes_trabajado,
        netos_sin_acuerdo: data.netos_sin_acuerdo,
        stats: data.stats,
      })
      // Adicionales que vienen agrupados en resumen.por_pagador
      const adsObj = {}
      ;(data.resumen?.por_pagador || []).forEach(p => {
        if (p.pagador_id) adsObj[p.pagador_id] = (p.adicionales || []).map(a => ({
          concepto: a.concepto || '',
          importe: a.importe ? fmt(Number(a.importe)) : '',
          _key: a.id || Math.random(),
        }))
      })
      setAdicionales(adsObj)
      // Compensaciones del backend → state
      setCompensaciones((data.resumen?.compensaciones || []).map(c => ({
        legajo: c.legajo,
        detalle1: c.detalle1 ?? c.detalle ?? c.paga ?? '',
        detalle2: c.detalle2 ?? '',
        multiplicador: c.multiplicador ?? 1,
        _key: c.id || Math.random(),
      })))
    } catch {
      setMsg('No se pudo cargar el tablero.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    (async () => {
      const ps = await cargarPeriodos()
      pagadoresApi.listar().then(({ data }) => setPagadores(data)).catch(() => {})
      if (ps.length > 0) {
        setPeriodoId(ps[0].id)
        // Default del form "+ Abrir mes" = último guardado + 1 (rollover dic→ene).
        const ult = ps[0]
        const nm  = ult.mes === 12 ? 1 : ult.mes + 1
        const na  = ult.mes === 12 ? ult.anio + 1 : ult.anio
        setNuevoMes({ mes: nm, anio: na })
      } else setLoading(false)
    })()
  }, [cargarPeriodos])

  useEffect(() => { if (periodoId) cargarTablero(periodoId) }, [periodoId, cargarTablero])

  // recálculo en vivo de cada fila
  const filasCalc = useMemo(() => filas.map(f => ({ ...f, ...calcFila(f, netosMap) })), [filas, netosMap])

  const resumen = useMemo(() => {
    const porPag = {}
    let tot = 0
    filasCalc.forEach(f => {
      const k = f.pagador_id || 0
      const nom = pagadores.find(p => p.id === f.pagador_id)?.nombre || 'Sin pagador'
      if (!porPag[k]) porPag[k] = { pagador: nom, total_general: 0 }
      porPag[k].total_general += f.total_general
      tot += f.total_general
    })
    return { porPag: Object.values(porPag), tot }
  }, [filasCalc, pagadores])

  const setCelda = (i, campo, val) => setFilas(fs => fs.map((f, j) => j === i ? { ...f, [campo]: val } : f))
  const agregar = () => setFilas(fs => [...fs, filaVacia()])
  const quitar = (i) => setFilas(fs => fs.filter((_, j) => j !== i))

  // Helpers de adicionales por pagador
  const setAdicional = (pagId, idx, campo, val) => setAdicionales(prev => ({
    ...prev,
    [pagId]: (prev[pagId] || []).map((a, j) => j === idx ? { ...a, [campo]: val } : a),
  }))
  const agregarAdicional = (pagId) => setAdicionales(prev => ({
    ...prev,
    [pagId]: [...(prev[pagId] || []), { concepto: '', importe: '', _key: Math.random() }],
  }))
  const quitarAdicional = (pagId, idx) => setAdicionales(prev => ({
    ...prev,
    [pagId]: (prev[pagId] || []).filter((_, j) => j !== idx),
  }))

  // Compensaciones de tesorería
  const setComp = (idx, campo, val) => setCompensaciones(prev => prev.map((c, j) => j === idx ? { ...c, [campo]: val } : c))
  const agregarComp = () => setCompensaciones(prev => [...prev, { legajo: '', detalle1: '', detalle2: '', multiplicador: 1.8, _key: Math.random() }])
  const quitarComp = (idx) => setCompensaciones(prev => prev.filter((_, j) => j !== idx))

  // Lookup vivo de los valores base de un legajo para compensaciones.
  // Fórmula: importe = banco × multiplicador + total_general.
  // El multiplicador (default 1.8) aplica SOLO al banco; el total general
  // se suma sin multiplicar.
  const valoresDeLegajo = (legajo) => {
    const f = filasCalc.find(x => String(x.legajo) === String(legajo))
    return f ? { banco: Number(f.banco) || 0, totalGeneral: Number(f.total_general) || 0 } : null
  }

  const guardar = async () => {
    setSaving(true); setMsg('')
    try {
      const payload = filas.map(f => ({
        acuerdo_id: f.acuerdo_id || null,
        legajo: f.legajo || null,
        legajo_referencia: f.legajo_referencia || null,
        nombre: f.nombre || null,
        coeficiente: f.coeficiente === '' ? 0 : Number(f.coeficiente),
        pagador_id: f.pagador_id || null,
        adelanto: Number(f.adelanto) || 0,
        adelanto_cash: Number(f.adelanto_cash) || 0,
        // state guarda dto en %; backend lo persiste como fracción.
        dto: (Number(f.dto) || 0) / 100,
        acuerdo_manual: (f.acuerdo_manual === '' || f.acuerdo_manual == null) ? null : Number(f.acuerdo_manual),
      }))
      const { data } = await sueadicApi.guardar(periodoId, payload)
      setFilas(data.filas.map(filaDelBackend)); setNetosMap(data.netos_map || {})

      // Guardar también los adicionales (en paralelo, mismo botón).
      const adicionalesFlat = Object.entries(adicionales).flatMap(([pid, arr]) =>
        (arr || []).map(a => ({
          pagador_id: Number(pid),
          concepto: (a.concepto || '').trim(),
          importe: parseNum(a.importe),
        }))
      ).filter(a => a.concepto || a.importe > 0)
      await sueadicApi.guardarAdicionales(periodoId, adicionalesFlat)

      // Compensaciones (filtro filas sin legajo)
      const compsFlat = compensaciones
        .filter(c => (c.legajo || '').toString().trim())
        .map(c => ({
          legajo:        c.legajo.toString().trim(),
          detalle1:      (c.detalle1 || '').trim(),
          detalle2:      (c.detalle2 || '').trim(),
          multiplicador: Number(c.multiplicador) || 1,
        }))
      const { data: data3 } = await sueadicApi.guardarCompensaciones(periodoId, compsFlat)

      // Refrescar adicionales y compensaciones desde el último response
      const adsObj = {}
      ;(data3.resumen?.por_pagador || []).forEach(p => {
        if (p.pagador_id) adsObj[p.pagador_id] = (p.adicionales || []).map(a => ({
          concepto: a.concepto || '',
          importe: a.importe ? fmt(Number(a.importe)) : '',
          _key: a.id || Math.random(),
        }))
      })
      setAdicionales(adsObj)
      setCompensaciones((data3.resumen?.compensaciones || []).map(c => ({
        legajo: c.legajo,
        detalle1: c.detalle1 ?? c.detalle ?? c.paga ?? '',
        detalle2: c.detalle2 ?? '',
        multiplicador: c.multiplicador ?? 1,
        _key: c.id || Math.random(),
      })))

      setMeta(m => ({ ...m, netos_sin_acuerdo: data.netos_sin_acuerdo, stats: data.stats }))
      setMsg('Guardado ✓')
    } catch (e) { setMsg(e.response?.data?.message || 'Error al guardar.') }
    finally { setSaving(false) }
  }

  const subirNetos = async () => {
    if (!netosTexto.trim()) return
    setSaving(true); setMsg('')
    try {
      const { data } = await sueadicApi.cargarNetos(periodoId, netosTexto)
      setMsg(`${data.cargados} netos cargados ✓`); setNetosTexto(''); setShowNetos(false)
      await cargarTablero(periodoId)
    } catch (e) { setMsg(e.response?.data?.message || 'Error al cargar netos.') }
    finally { setSaving(false) }
  }

  const importarTablero = async () => {
    if (!importarTexto.trim()) return
    setSaving(true); setMsg('')
    try {
      const { data } = await sueadicApi.importarAcuerdos(importarTexto)
      setMsg(`Tablero: ${data.agregados} agregado(s), ${data.actualizados} actualizado(s) ✓`)
      setImportarTexto(''); setShowImportar(false)
      await cargarTablero(periodoId)
    } catch (e) { setMsg(e.response?.data?.message || 'Error al importar tablero.') }
    finally { setSaving(false) }
  }

  const vaciarTablero = async () => {
    if (!confirmType('¿Vaciar TODO el tablero base? Borra todos los acuerdos y ajustes (irreversible). Los netos del banco y los pagadores quedan intactos.')) return
    setSaving(true); setMsg('')
    try {
      const { data } = await sueadicApi.vaciarTablero()
      setMsg(`Tablero vaciado (${data.borrados} acuerdo(s) eliminado(s)) ✓`)
      await cargarTablero(periodoId)
    } catch (e) { setMsg(e.response?.data?.message || 'Error al vaciar tablero.') }
    finally { setSaving(false) }
  }

  // Vacía solo el mes actual (netos + ajustes + adicionales + minuta si la
  // hubiera de un cierre previo), y rehereda los adicionales del mes anterior.
  // Sirve para "arrancar de cero" cuando el mes recién abierto quedó mal cargado.
  const vaciarMes = async () => {
    if (!window.confirm('¿Vaciar este mes? Borra netos del banco, adelantos, dto y adicionales — queda como un mes recién abierto. Acuerdos y compensaciones (que son globales) NO se tocan.')) return
    setSaving(true); setMsg('')
    try {
      await sueadicApi.vaciar(periodoId)
      await cargarTablero(periodoId)
      setMsg('Mes vaciado ✓')
    } catch (e) { setMsg(e.response?.data?.message || 'No se pudo vaciar.') }
    finally { setSaving(false) }
  }

  const crearMes = async () => {
    try {
      const { data } = await sueadicApi.crearPeriodo(nuevoMes.anio, nuevoMes.mes, (nuevoMes.etiqueta || '').trim() || undefined)
      setNuevoMes(m => ({ ...m, etiqueta: '' }))
      await cargarPeriodos(); setPeriodoId(data.id)
    } catch (e) { setMsg(e.response?.data?.message || 'No se pudo crear el mes.') }
  }

  const cerrarMes = async () => {
    setSaving(true); setMsg('')
    try {
      // Persiste tablero + adicionales + compensaciones antes de congelar
      // el snapshot (si no guardamos, se perderían los cambios en pantalla).
      await guardar()
      await sueadicApi.cerrar(periodoId)
      await cargarPeriodos()
      await cargarTablero(periodoId)
      setMsg('Mes cerrado ✓')
    } catch (e) { setMsg(e.response?.data?.message || 'No se pudo cerrar.') }
    finally { setSaving(false) }
  }

  const reabrirMes = async () => {
    try { await sueadicApi.reabrir(periodoId); await cargarPeriodos(); await cargarTablero(periodoId); setMsg('Mes reabierto ✓') }
    catch (e) { setMsg(e.response?.data?.message || 'No se pudo reabrir.') }
  }

  // Elimina un período completo (netos, ajustes, adicionales, compensaciones
  // del mes, minuta si la tuviera). NO toca acuerdos ni compensaciones si
  // fueran globales — los del mes se van con él. Solo se puede sobre un mes
  // abierto: si está cerrado, hay que reabrir primero.
  const eliminarMes = async () => {
    const activo = periodos.find(p => p.id === periodoId)
    if (!activo) return
    if (activo.estado === 'cerrado') {
      setMsg('Reabrí el mes antes de eliminarlo.'); return
    }
    if (!confirmType(`¿Eliminar "${activo.etiqueta}" definitivamente? Se borra el mes con todos sus datos (netos, ajustes, adicionales, compensaciones). Los acuerdos base y los datos de otros meses NO se tocan.`)) return
    try {
      await sueadicApi.eliminarPeriodo(periodoId)
      const restantes = periodos.filter(p => p.id !== periodoId)
      await cargarPeriodos()
      // Si queda algún otro mes, saltamos al primero (por defecto el más reciente).
      if (restantes.length) setPeriodoId(restantes[0].id)
      else setPeriodoId(null)
      setMsg('Mes eliminado ✓')
    } catch (e) { setMsg(e.response?.data?.message || 'No se pudo eliminar.') }
  }

  // Renombrar período: cambia mes/año del período activo. Útil si se creó
  // el mes equivocado (ej: Junio en vez de Mayo) y ya tiene datos cargados.
  const editarNombre = async () => {
    const p = periodos.find(x => x.id === periodoId)
    if (!p) return
    // Nombre libre (ej. "SAC 2026", "Aguinaldo Junio"). Mantiene el mes/año.
    const nombre = window.prompt('Nombre del período (ej. "SAC 2026"):', p.etiqueta)
    if (nombre === null) return                  // canceló
    if (!nombre.trim()) { setMsg('El nombre no puede estar vacío.'); return }
    try {
      await sueadicApi.renombrarPeriodo(periodoId, p.anio, p.mes, nombre.trim())
      await cargarPeriodos()
      await cargarTablero(periodoId)
      setMsg('Período renombrado ✓')
    } catch (e) { setMsg(e.response?.data?.message || 'No se pudo renombrar.') }
  }

  const periodoActivo = periodos.find(p => p.id === periodoId)
  const etiquetaActiva = periodoActivo ? `${periodoActivo.etiqueta}${periodoActivo.estado === 'cerrado' ? ' 🔒' : ''}` : ''

  // KPIs (cálculo inline, baratos sobre los datos en pantalla)
  const totalAgentes = resumen.tot
  const totalAdicionales = Object.values(adicionales).reduce((acc, arr) =>
    acc + (arr || []).reduce((s, a) => s + (parseNum(a.importe)), 0), 0)
  const totalTeso = totalAgentes + totalAdicionales
  const compsActivas = compensaciones.filter(c => (c.legajo || '').toString().trim())
  const totalComps = compsActivas.reduce((acc, c) => {
    const v = valoresDeLegajo(c.legajo)
    if (!v) return acc
    const mult = Number(c.multiplicador) || 1
    return acc + (v.banco * mult + v.totalGeneral)
  }, 0)

  return (
    <div style={st.page}>
    <div style={st.container}>
      <div style={st.head}>
        <div>
          <div style={st.eyebrow}>RRHH · Liquidación</div>
          <h2 style={st.title}>SueAdic{etiquetaActiva && <span style={st.titleMes}>· {etiquetaActiva}</span>}</h2>
        </div>
        <div style={st.headRight}>
          <div style={st.periodSel}>
            <span style={st.periodIcon}>📅</span>
            <select style={st.sel} value={periodoId || ''} onChange={e => setPeriodoId(Number(e.target.value))}>
              {periodos.length === 0 && <option value="">— sin meses —</option>}
              {periodos.map(p => <option key={p.id} value={p.id}>{p.etiqueta}{p.estado === 'cerrado' ? ' 🔒' : ''}</option>)}
            </select>
          </div>
          {periodoId && (
            <button style={st.btn} onClick={editarNombre} title="Cambiar el mes/año del período (sin perder datos)">
              ✏️ Editar
            </button>
          )}
        </div>
      </div>

      {/* Nuevo mes — el período se guarda con el mes trabajado.
          Default: último mes guardado + 1 (editable). */}
      <div style={st.nuevoMes}>
        <span style={st.abrirLabel}>Nuevo mes:</span>
        <select style={st.selSm} value={nuevoMes.mes} onChange={e => setNuevoMes(m => ({ ...m, mes: Number(e.target.value) }))}>
          {MESES.slice(1).map((nm, i) => <option key={i + 1} value={i + 1}>{nm}</option>)}
        </select>
        <input style={st.inpSm} type="number" value={nuevoMes.anio} onChange={e => setNuevoMes(m => ({ ...m, anio: Number(e.target.value) }))} />
        <input style={{ ...st.inpSm, width: 140 }} placeholder="Nombre (opcional)" value={nuevoMes.etiqueta || ''} onChange={e => setNuevoMes(m => ({ ...m, etiqueta: e.target.value }))} />
        <button style={st.btnGhost} onClick={crearMes}>+ Nuevo mes</button>
      </div>

      {msg && <div style={st.msg}>{msg}</div>}

      {loading ? <p style={{ color: '#94a3b8' }}>Cargando…</p> : !periodoId ? (
        <p style={{ color: '#64748b' }}>No hay meses. Abrí el primero arriba.</p>
      ) : (
        <>
          {/* KPIs */}
          <div style={st.kpis}>
            <div style={st.kpi}>
              <div style={st.kpiLabel}>Liquidación</div>
              <div style={st.kpiValue}>$ {fmt(totalAgentes)}</div>
              <div style={st.kpiSub}>{meta?.stats?.filas || 0} legajos · {resumen.porPag.length} pagadores</div>
            </div>
            <div style={st.kpi}>
              <div style={st.kpiLabel}>Adicionales</div>
              <div style={st.kpiValue}>$ {fmt(totalAdicionales)}</div>
              <div style={st.kpiSub}>{Object.values(adicionales).reduce((acc, arr) => acc + (arr || []).filter(a => a.concepto || a.importe > 0).length, 0)} conceptos</div>
            </div>
            <div style={{ ...st.kpi, ...st.kpiAccent }}>
              <div style={{ ...st.kpiLabel, ...st.kpiAccentLbl }}>Total general</div>
              <div style={{ ...st.kpiValue, ...st.kpiAccentVal }}>$ {fmt(totalTeso)}</div>
              <div style={{ ...st.kpiSub, ...st.kpiAccentLbl }}>{meta?.periodo?.etiqueta}</div>
            </div>
          </div>

          {/* Acciones del mes */}
          <div style={st.acciones}>
            <button style={st.btn} onClick={() => setShowNetos(s => !s)} disabled={cerrado}>📋 Cargar netos del banco</button>
            <button style={st.btn} onClick={agregar} disabled={cerrado}>+ Fila</button>
            <button style={{ ...st.btnPrimary, opacity: cerrado ? 0.5 : 1 }} onClick={guardar} disabled={cerrado || saving}>{saving ? 'Guardando…' : '💾 Guardar tablero'}</button>
            {!cerrado
              ? <button style={st.btn} onClick={cerrarMes} title="Cerrar el mes (congela el snapshot)">🔓 Abierto</button>
              : <button style={st.btnDanger} onClick={reabrirMes} title="Reabrir el mes (para editar)">🔒 Cerrado</button>}
            <button style={st.btn} onClick={() => window.print()}>🖨 Imprimir</button>
            <button
              style={{ ...st.btn, padding: '7px 10px', color: '#8a8a8a' }}
              onClick={() => setShowAvanzado(s => !s)}
              disabled={cerrado}
              title="Acciones poco frecuentes"
            >⋯</button>
            {cerrado && <span style={st.cerradoTag}>MES CERRADO</span>}
            <span style={{ color: '#8a8a8a', fontSize: 12, marginLeft: 'auto' }}>
              {meta?.stats?.con_neto}/{meta?.stats?.filas} con neto · {meta?.stats?.netos} netos cargados
            </span>
          </div>

          {showAvanzado && !cerrado && (
            <div style={st.avanzadoBox}>
              <span style={st.avanzadoNota}>⚠ acciones poco frecuentes — usar con cuidado:</span>
              <button style={{ ...st.btn, fontSize: '0.78rem' }} onClick={() => { setShowImportar(s => !s); setShowAvanzado(false) }}>📥 Importar tablero</button>
              <button style={{ ...st.btn, fontSize: '0.78rem', color: '#92400e' }} onClick={() => { vaciarMes(); setShowAvanzado(false) }} disabled={saving} title="Borra netos + ajustes + adicionales del mes. Queda como recién abierto.">🧹 Vaciar mes</button>
              <button style={{ ...st.btn, fontSize: '0.78rem', color: '#b91c1c' }} onClick={() => { eliminarMes(); setShowAvanzado(false) }} disabled={saving} title="Borra el período completo (todos sus datos). Los acuerdos base y los otros meses NO se tocan.">🗑 Eliminar este mes</button>
              <button style={{ ...st.btn, fontSize: '0.78rem', color: '#92400e' }} onClick={() => { vaciarTablero(); setShowAvanzado(false) }} disabled={saving} title="Borra el tablero base entero (acuerdos globales). Distinto de vaciar mes.">🗑 Vaciar tablero base</button>
            </div>
          )}

          {showImportar && !cerrado && (
            <div style={st.netosBox}>
              <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: '#475569' }}>
                Pegá la lista de coeficientes (<b>legajo · nombre · coeficiente</b>, desde Excel). Upsert por legajo: si existe lo actualiza, si no lo agrega. No borra filas existentes.
              </p>
              <textarea style={st.textarea} value={importarTexto} onChange={e => setImportarTexto(e.target.value)} placeholder={'12345\tAPELLIDO NOMBRE\t0,18\n12346\tOTRO NOMBRE\t0,22'} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button style={st.btnPrimary} onClick={importarTablero} disabled={saving}>Importar</button>
                <button style={st.btnGhost} onClick={() => setShowImportar(false)}>Cancelar</button>
              </div>
            </div>
          )}

          {showNetos && !cerrado && (
            <div style={st.netosBox}>
              <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: '#475569' }}>Pegá la nómina del banco (LEGAJO · EMPLEADO · PERIODO · NETOS, desde Excel). Reemplaza los netos del mes.</p>
              <textarea style={st.textarea} value={netosTexto} onChange={e => setNetosTexto(e.target.value)} placeholder={'12345\tAPELLIDO NOMBRE\tAbril 2026\t1.234.567,89'} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button style={st.btnPrimary} onClick={subirNetos} disabled={saving}>Cargar</button>
                <button style={st.btnGhost} onClick={() => setShowNetos(false)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Tablero — 13 columnas + acción al inicio */}
          <div style={st.tablaWrap}>
            <div style={{ overflowX: 'auto' }}>
            <table style={st.table}>
              <thead><tr>
                {['', 'Legajo', 'Ref', 'Nombre', 'Periodo', 'Banco', 'Total banco', 'Coef.', 'Acuerdo', 'Pagador', 'Dto %', 'En mano', 'Total general', ''].map((h, i) => {
                  const base = [5, 6, 8, 10, 11, 12].includes(i) ? st.thR : st.th
                  const sticky = i === 0 ? { position: 'sticky', left: 0, zIndex: 3, minWidth: 40 } : null
                  return <th key={i} style={{ ...base, ...sticky }}>{h}</th>
                })}
              </tr></thead>
              <tbody>
                {filasCalc.map((f, i) => {
                  const rowBg = f.falta_banco ? '#fffbeb' : (i % 2 === 1 ? C.rowAlt : C.surface)
                  const hasAdel = (Number(f.adelanto) || 0) > 0 || (Number(f.adelanto_cash) || 0) > 0
                  const adelOpen = adelExpand.has(i) || hasAdel
                  const toggleAdel = () => setAdelExpand(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n })
                  return (
                  <React.Fragment key={i}>
                  <tr style={{ background: rowBg }}>
                    <td style={{ ...st.tdC, position: 'sticky', left: 0, background: rowBg, zIndex: 1 }}>
                      {!cerrado && (
                        <button
                          style={st.delBtn}
                          onClick={() => { if (confirmType('¿Eliminar este agente del tablero? El acuerdo (legajo + pagador + coef + dto) se borra de forma permanente.')) quitar(i) }}
                          title="Eliminar fila"
                        >×</button>
                      )}
                    </td>
                    <td style={{ ...st.tdL, ...st.legajo }}><input style={{ ...st.cellInp, width: 64, textAlign: 'left', fontVariantNumeric: 'tabular-nums' }} value={f.legajo ?? ''} onChange={e => setCelda(i, 'legajo', e.target.value)} disabled={cerrado} /></td>
                    <td style={{ ...st.tdL }}>
                      <input
                        style={{ ...st.cellInp, width: 56, textAlign: 'left', color: C.inkSoft, fontStyle: f.legajo_referencia ? 'italic' : 'normal', fontVariantNumeric: 'tabular-nums' }}
                        value={f.legajo_referencia ?? ''}
                        onChange={e => setCelda(i, 'legajo_referencia', e.target.value)}
                        disabled={cerrado}
                        placeholder="—"
                        title="Legajo de referencia (paritarias): si se completa, el banco se toma del legajo indicado en vez del propio. Ej: Diego con ref=951 indexa por el banco de Brites."
                      />
                    </td>
                    <td style={{ ...st.tdL, ...st.nombre }}><input style={{ ...st.cellInp, width: 280, textAlign: 'left', fontWeight: 600 }} value={f.nombre ?? ''} onChange={e => setCelda(i, 'nombre', e.target.value)} disabled={cerrado} placeholder={netosMap[String(f.legajo)]?.nombre || ''} /></td>
                    <td style={st.tdL}>
                      <span style={st.periodoChip}>{netosMap[String(f.legajo)]?.periodo || meta?.mes_trabajado || '—'}</span>
                    </td>
                    <td style={st.tdR}>{fmt(f.banco)}</td>
                    <td style={st.tdR}>{fmt(f.total_banco)}</td>
                    <td style={st.tdR}><input style={{ ...st.cellInp, width: 60 }} value={f.coeficiente ?? ''} onChange={e => setCelda(i, 'coeficiente', e.target.value)} disabled={cerrado} placeholder="0.00" /></td>
                    <td style={st.tdR}>
                      <input
                        style={{ ...st.cellInp, width: 100, background: f.tiene_acuerdo_manual ? C.warnSoft : 'transparent' }}
                        value={f.acuerdo_manual ?? ''}
                        onChange={e => setCelda(i, 'acuerdo_manual', e.target.value)}
                        disabled={cerrado}
                        placeholder={fmt(f.acuerdo)}
                        title={f.tiene_acuerdo_manual ? 'Acuerdo cargado a mano (override del cálculo)' : 'Vacío = se calcula automático. Cargá un monto para sobreescribir.'}
                      />
                    </td>
                    <td style={st.tdL}>
                      <select style={st.cellSel} value={f.pagador_id || ''} onChange={e => setCelda(i, 'pagador_id', e.target.value ? Number(e.target.value) : '')} disabled={cerrado}>
                        <option value="">—</option>
                        {pagadores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </td>
                    <td style={st.tdR}><input style={{ ...st.cellInp, width: 50 }} value={f.dto ?? ''} onChange={e => setCelda(i, 'dto', e.target.value)} disabled={cerrado} /></td>
                    <td style={st.tdR}>{fmt(f.en_mano)}</td>
                    <td style={{ ...st.tdR, ...st.total }}>{fmt(f.total_general)}</td>
                    <td style={{ ...st.tdC, width: 22 }}>
                      {!cerrado && (
                        <button
                          style={{ ...st.delBtn, color: hasAdel ? C.accent : C.inkMute, borderColor: hasAdel ? C.accent : C.border }}
                          onClick={toggleAdel}
                          title={adelOpen ? 'Cerrar adelantos' : 'Agregar adelanto / cash'}
                        >{adelOpen ? '–' : '+'}</button>
                      )}
                    </td>
                  </tr>
                  {adelOpen && (
                    <tr style={{ background: rowBg }}>
                      <td colSpan={14} style={{ padding: '0 8px 4px 52px', borderBottom: `1px solid ${C.borderSoft}` }}>
                        <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center', fontSize: 11, color: C.inkSoft }}>
                          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                            <span>Adelanto:</span>
                            <input style={{ ...st.cellInp, width: 90, textAlign: 'right', border: `1px solid ${C.border}`, borderRadius: 5, background: C.surface }} value={f.adelanto ?? ''} onChange={e => setCelda(i, 'adelanto', e.target.value)} disabled={cerrado} placeholder="0" />
                          </label>
                          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                            <span>Adelanto cash:</span>
                            <input style={{ ...st.cellInp, width: 90, textAlign: 'right', border: `1px solid ${C.border}`, borderRadius: 5, background: C.surface }} value={f.adelanto_cash ?? ''} onChange={e => setCelda(i, 'adelanto_cash', e.target.value)} disabled={cerrado} placeholder="0" />
                          </label>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  )
                })}
                {filasCalc.length === 0 && <tr><td colSpan={14} style={{ ...st.tdC, color: C.inkMute, padding: '1.5rem' }}>Tablero vacío. Agregá filas o cargá netos.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>

          {meta?.netos_sin_acuerdo?.length > 0 && (
            <div style={st.aviso}>
              <b>ℹ {meta.netos_sin_acuerdo.length} legajo(s) en la nómina sin fila en el tablero:</b>{' '}
              {meta.netos_sin_acuerdo.slice(0, 30).map((n, i) => <span key={i} style={st.chip}>{n.legajo}{n.nombre ? ` ${n.nombre}` : ''}</span>)}
            </div>
          )}

          {/* Resumen por pagador (debajo del tablero) */}
          <div style={st.resumenSecc}>
            <h3 style={st.resumenTitulo}>Resumen por pagador</h3>
            <div style={st.resumenGrid}>
              {pagadores.map(pag => {
                const agentes = filasCalc.filter(f => Number(f.pagador_id) === pag.id)
                const totalAgentes = agentes.reduce((acc, f) => acc + (f.total_general || 0), 0)
                const ads = adicionales[pag.id] || []
                const totalAdicionales = ads.reduce((acc, a) => acc + (parseNum(a.importe)), 0)
                const totalPagador = totalAgentes + totalAdicionales
                return (
                  <div key={pag.id} style={st.pagadorBox}>
                    <div style={st.pagadorHead}>
                      <h3 style={st.pagadorH3}>{pag.nombre}</h3>
                      {agentes.length > 0 && <span style={st.pagadorBadge}>× {agentes.length}</span>}
                    </div>

                    {/* Lista */}
                    {agentes.length > 0 && (
                      <table style={st.miniTabla}>
                        <tbody>
                          {agentes.map((a, k) => (
                            <tr key={k}>
                              <td style={st.miniCelda}>{a.nombre || '—'}</td>
                              <td style={st.miniCeldaVal}>{fmt(a.total_general)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td style={st.subtotalLbl}>Subtotal</td>
                            <td style={st.subtotalVal}>{fmt(totalAgentes)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {/* Adicionales */}
                    <div style={st.adicTitulo}>Adicionales</div>
                    {ads.map((a, k) => (
                      <div key={a._key ?? k} style={st.adicRow}>
                        <input
                          style={st.adicInp}
                          value={a.concepto ?? ''}
                          onChange={e => setAdicional(pag.id, k, 'concepto', e.target.value)}
                          placeholder="Concepto"
                          disabled={cerrado}
                        />
                        <input
                          style={st.adicInpVal}
                          value={a.importe ?? ''}
                          onChange={e => setAdicional(pag.id, k, 'importe', e.target.value)}
                          onFocus={e => { const n = parseNum(a.importe); if (n) setAdicional(pag.id, k, 'importe', String(n)) }}
                          onBlur={e => { const n = parseNum(a.importe); setAdicional(pag.id, k, 'importe', n ? fmt(n) : '') }}
                          placeholder="0.00"
                          disabled={cerrado}
                        />
                        {!cerrado
                          ? <button style={st.delBtn} onClick={() => quitarAdicional(pag.id, k)} title="Quitar">×</button>
                          : <span />}
                      </div>
                    ))}
                    {!cerrado && (
                      <div style={{ padding: '4px 0' }}>
                        <button style={{ ...st.btnGhost, fontSize: 11.5, padding: '4px 10px' }} onClick={() => agregarAdicional(pag.id)}>+ Concepto</button>
                      </div>
                    )}
                    {ads.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', ...st.subtotalLbl, padding: '4px 0 2px' }}>
                        <span>Subtotal</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totalAdicionales)}</span>
                      </div>
                    )}

                    {/* Total */}
                    <div style={st.pagadorTotal}>
                      <span style={st.pagadorTotalLbl}>{pag.nombre}</span>
                      <span>$ {fmt(totalPagador)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Resumen (agentes pagados por mí vía Tesorería) + Registración de
              Presea + Asientos de compensación — 3 cuadros en un renglón de 5cm c/u. */}
          {(() => {
            const totalesPagadores = pagadores.map(pag => ({
              id: pag.id,
              nombre: pag.nombre,
              agentes: filasCalc.filter(f => Number(f.pagador_id) === pag.id),
              total: filasCalc.filter(f => Number(f.pagador_id) === pag.id).reduce((acc, f) => acc + (f.total_general || 0), 0)
                   + (adicionales[pag.id] || []).reduce((acc, a) => acc + (parseNum(a.importe)), 0),
            }))
            const totalTesoreria = totalesPagadores.reduce((acc, p) => acc + p.total, 0)
            // Σ importes de compensaciones (cada una es par −/+ del mismo monto).
            const sumImportes = compensaciones.reduce((acc, c) => {
              const v = valoresDeLegajo(c.legajo)
              if (!v) return acc
              const mult = Number(c.multiplicador) || 1
              return acc + (v.banco * mult + v.totalGeneral)
            }, 0)
            const totalRegistrar = totalTesoreria - sumImportes + sumImportes
            return (
              <div style={st.bottomGrid}>
                {/* (1) Resumen — Martin con detalle por agente; los demás solo total */}
                <div style={st.tesoBox}>
                  <div style={st.tesoTit}>Resumen</div>
                  {totalesPagadores.map(p => {
                    const esMartin = /martin/i.test(p.nombre || '')
                    if (esMartin) {
                      return (
                        <div key={p.id}>
                          <div style={st.tesoGroupTit}>{p.nombre}</div>
                          {p.agentes.map((a, k) => (
                            <div key={k} style={st.tesoLinSm}>
                              <span style={st.tesoNameSm} title={a.nombre || ''}>{a.nombre || '—'}</span>
                              <span style={st.tesoNumSm}>{fmt(a.total_general)}</span>
                            </div>
                          ))}
                          {p.agentes.length === 0 && (
                            <div style={{ ...st.tesoLinSm, color: C.inkMute, fontStyle: 'italic' }}>—</div>
                          )}
                        </div>
                      )
                    }
                    return (
                      <div key={p.id} style={st.tesoLin}>
                        <span style={st.tesoLbl}>{p.nombre}</span>
                        <span style={st.tesoNum}>{fmt(p.total)}</span>
                      </div>
                    )
                  })}
                  <div style={st.tesoTot}>
                    <span style={{ fontSize: 11, color: C.inkSoft, fontWeight: 600 }}>Total</span>
                    <span>$ {fmt(totalTesoreria)}</span>
                  </div>
                </div>

                {/* (2) Registración de Presea */}
                <div style={st.tesoBox}>
                  <div style={st.tesoTit}>Registración en Presea</div>
                  <div style={st.tesoLin}>
                    <span style={st.tesoLbl}>Total general</span>
                    <span style={st.tesoNum}>{fmt(totalTesoreria)}</span>
                  </div>
                  <div style={st.tesoLin}>
                    <span style={st.tesoLbl}>− Compensaciones</span>
                    <span style={{ ...st.tesoNum, color: C.inkSoft }}>{fmt(sumImportes)}</span>
                  </div>
                  <div style={st.tesoLin}>
                    <span style={st.tesoLbl}>+ Compensaciones</span>
                    <span style={{ ...st.tesoNum, color: C.good }}>{fmt(sumImportes)}</span>
                  </div>
                  <div style={st.tesoTot}>
                    <span style={{ fontSize: 11, color: C.inkSoft, fontWeight: 600 }}>A registrar</span>
                    <span>$ {fmt(totalRegistrar)}</span>
                  </div>
                </div>

                {/* (3) Asientos de compensación — editor en el mismo renglón */}
                <div style={st.tesoBox}>
                  <div style={st.tesoTit}>Compensaciones</div>
                  {compensaciones.map((c, i) => {
                    const v = valoresDeLegajo(c.legajo)
                    const mult = Number(c.multiplicador) || 1
                    const imp = v ? v.banco * mult + v.totalGeneral : null
                    const notFound = c.legajo && !v
                    return (
                      <div key={c._key ?? i} style={st.tesoComp}>
                        <div style={st.tesoCompInputs}>
                          <input
                            style={{ ...st.tesoInp, width: 50, background: notFound ? C.warnSoft : C.surface }}
                            value={c.legajo ?? ''}
                            onChange={e => setComp(i, 'legajo', e.target.value)}
                            placeholder="leg"
                            disabled={cerrado}
                            title={notFound ? 'Legajo no encontrado en el tablero' : ''}
                          />
                          <input
                            style={{ ...st.tesoInp, width: 44, textAlign: 'right' }}
                            value={c.multiplicador ?? 1}
                            onChange={e => setComp(i, 'multiplicador', e.target.value)}
                            placeholder="×"
                            disabled={cerrado}
                            title="Multiplicador (default 1.8)"
                          />
                          {!cerrado && <button style={st.delBtn} onClick={() => quitarComp(i)} title="Quitar">×</button>}
                        </div>
                        <div style={st.tesoLinIn}>
                          <input
                            style={{ ...st.tesoInp, flex: 1, minWidth: 0 }}
                            value={c.detalle1 ?? ''}
                            onChange={e => setComp(i, 'detalle1', e.target.value)}
                            placeholder="−"
                            disabled={cerrado}
                          />
                        </div>
                        <div style={{ ...st.tesoLinIn, justifyContent: 'flex-end' }}>
                          <span style={{ ...st.tesoNum, color: C.inkSoft, fontSize: 11 }}>
                            {imp != null ? `− ${fmt(imp)}` : ''}
                          </span>
                        </div>
                        <div style={st.tesoLinIn}>
                          <input
                            style={{ ...st.tesoInp, flex: 1, minWidth: 0 }}
                            value={c.detalle2 ?? ''}
                            onChange={e => setComp(i, 'detalle2', e.target.value)}
                            placeholder="+"
                            disabled={cerrado}
                          />
                        </div>
                        <div style={{ ...st.tesoLinIn, justifyContent: 'flex-end' }}>
                          <span style={{ ...st.tesoNum, color: C.good, fontSize: 11 }}>
                            {imp != null ? `+ ${fmt(imp)}` : ''}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                  {!cerrado && (
                    <div style={{ padding: '4px 0 0' }}>
                      <button style={{ ...st.btnGhost, fontSize: 11, padding: '3px 8px', width: '100%' }} onClick={agregarComp}>+ Comp.</button>
                    </div>
                  )}
                  {compensaciones.length === 0 && cerrado && (
                    <div style={{ ...st.tesoSub, padding: '4px 0' }}>(sin compensaciones)</div>
                  )}
                </div>
              </div>
            )
          })()}
        </>
      )}
    </div>
    </div>
  )
}

// Paleta y tokens — propuesta A "Editorial".
// Crema cálido + azul profundo. Sin tipografías externas.
const C = {
  bg: '#f5f4f0',
  surface: '#ffffff',
  ink: '#1a1a1a',
  inkSoft: '#525252',
  inkMute: '#8a8a8a',
  border: '#e7e5e0',
  borderSoft: '#f0eeea',
  accent: '#1e3a8a',
  accentSoft: '#eef2ff',
  good: '#166534',
  goodSoft: '#ecfdf5',
  warn: '#92400e',
  warnSoft: '#fffbeb',
  rowAlt: '#faf9f6',
}

const st = {
  confi: { display: 'none' },

  // ── Page wrapper ────────────────────────────────────────
  page: { background: C.bg, minHeight: '100vh', padding: '24px 32px', margin: '-16px -20px', boxSizing: 'border-box' },
  container: { maxWidth: 1480, margin: '0 auto' },

  // ── Header ──────────────────────────────────────────────
  head: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, paddingBottom: 14, borderBottom: `1px solid ${C.border}`, marginBottom: 18, flexWrap: 'wrap' },
  eyebrow: { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.inkMute, marginBottom: 4 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' },
  titleMes: { fontWeight: 500, color: C.inkSoft, marginLeft: 6 },
  headRight: { display: 'flex', alignItems: 'center', gap: 10 },
  periodSel: { display: 'flex', alignItems: 'center', gap: 8, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '7px 12px' },
  periodIcon: { color: C.inkMute, fontSize: 13 },
  sel: { border: 0, background: 'transparent', font: 'inherit', color: C.ink, fontWeight: 600, fontSize: 13, cursor: 'pointer', outline: 'none' },

  // ── Abrir mes ───────────────────────────────────────────
  nuevoMes: { display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px' },
  abrirLabel: { color: C.inkMute, fontSize: 12 },
  selSm: { padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12.5, background: C.surface, color: C.ink, fontWeight: 500 },
  inpSm: { padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, width: 70, fontSize: 12.5, background: C.surface, fontVariantNumeric: 'tabular-nums' },

  // ── Msgs ────────────────────────────────────────────────
  msg: { background: C.accentSoft, border: `1px solid ${C.accent}33`, color: C.accent, borderRadius: 8, padding: '6px 12px', fontSize: 12.5, margin: '6px 0', fontWeight: 500 },

  // ── KPI strip ───────────────────────────────────────────
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 },
  kpi: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px' },
  kpiLabel: { fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.inkMute },
  kpiValue: { fontSize: 18, fontWeight: 700, marginTop: 2, color: C.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' },
  kpiSub: { fontSize: 10.5, color: C.inkMute, marginTop: 1 },
  kpiAccent: { background: C.accent, color: 'white', border: 'none' },
  kpiAccentLbl: { color: 'rgba(255,255,255,0.7)' },
  kpiAccentVal: { color: 'white' },

  // ── Acciones ────────────────────────────────────────────
  acciones: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 12px' },
  btn: { background: C.surface, border: `1px solid ${C.border}`, color: C.ink, borderRadius: 8, padding: '7px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5 },
  btnGhost: { background: 'transparent', border: `1px dashed ${C.border}`, color: C.ink, borderRadius: 8, padding: '7px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12.5 },
  btnPrimary: { background: C.accent, color: 'white', border: `1px solid ${C.accent}`, borderRadius: 8, padding: '7px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12.5 },
  btnDanger: { background: C.surface, border: `1px solid ${C.border}`, color: C.warn, borderRadius: 8, padding: '7px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12.5 },
  cerradoTag: { background: C.borderSoft, color: C.inkSoft, borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' },
  netosBox: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, margin: '4px 0 14px' },
  avanzadoBox: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: C.warnSoft, border: `1px solid ${C.warn}33`, borderRadius: 8, padding: '8px 12px', margin: '4px 0 12px' },
  avanzadoNota: { color: C.warn, fontSize: 11.5, fontWeight: 600 },
  textarea: { width: '100%', minHeight: 90, padding: 8, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12, boxSizing: 'border-box', background: C.surface, color: C.ink },
  aviso: { background: C.accentSoft, border: `1px solid ${C.accent}33`, borderRadius: 8, padding: '7px 12px', fontSize: 12, color: C.accent, marginTop: 8 },

  // ── Tabla principal ─────────────────────────────────────
  tablaWrap: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  th: { background: C.surface, color: C.inkMute, fontWeight: 600, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '7px 8px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0 },
  thR: { background: C.surface, color: C.inkMute, fontWeight: 600, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '7px 8px', textAlign: 'right', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0 },
  tr: {},
  trAlt: { background: C.rowAlt },
  tdEdit: { padding: '3px 4px', borderBottom: `1px solid ${C.borderSoft}` },
  tdR: { padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${C.borderSoft}` },
  tdC: { padding: '4px 8px', textAlign: 'center', borderBottom: `1px solid ${C.borderSoft}` },
  tdL: { padding: '4px 8px', textAlign: 'left', borderBottom: `1px solid ${C.borderSoft}` },
  legajo: { color: C.inkMute, fontWeight: 500 },
  nombre: { fontWeight: 600, color: C.ink },
  total: { fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' },
  periodoChip: { fontSize: 10.5, color: C.inkSoft, background: C.borderSoft, padding: '1px 7px', borderRadius: 999, display: 'inline-block' },
  pagadorChip: { fontSize: 10.5, fontWeight: 600, color: C.accent, background: C.accentSoft, padding: '2px 8px', borderRadius: 999, display: 'inline-block' },
  cellInp: { width: 88, padding: '2px 5px', border: 0, borderRadius: 4, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', textAlign: 'right', background: 'transparent', color: C.ink, outline: 'none' },
  cellSel: { width: 120, padding: '2px 5px', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, background: C.surface, color: C.ink },
  del: { background: 'transparent', color: C.inkMute, border: `1px solid ${C.border}`, borderRadius: 5, width: 20, height: 20, cursor: 'pointer', fontWeight: 700, fontSize: 11, lineHeight: 1, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  delBtn: { background: 'transparent', color: C.inkMute, border: `1px solid ${C.border}`, borderRadius: 5, width: 20, height: 20, cursor: 'pointer', fontWeight: 700, fontSize: 11, lineHeight: 1, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },

  // ── Sección + título ────────────────────────────────────
  seccTit: { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.inkMute, margin: '14px 0 12px' },

  // ── Resumen por pagador (cards) ────────────────────────
  resumenSecc: { marginTop: 2 },
  resumenTitulo: { fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.inkMute, margin: '10px 0 8px' },
  resumenGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginBottom: 16 },
  pagadorBox: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column' },
  pagadorHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 },
  pagadorH3: { margin: 0, fontSize: 13, fontWeight: 700, color: C.ink, letterSpacing: '0.02em' },
  pagadorBadge: { fontSize: 11, fontWeight: 600, color: C.inkMute, fontVariantNumeric: 'tabular-nums' },
  miniTabla: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  miniCelda: { padding: '2px 0', borderBottom: `1px solid ${C.borderSoft}`, color: C.inkSoft },
  miniCeldaVal: { padding: '2px 0', borderBottom: `1px solid ${C.borderSoft}`, color: C.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 },
  adicTitulo: { marginTop: 6, fontSize: 10, fontWeight: 600, color: C.inkMute, textTransform: 'uppercase', letterSpacing: '0.08em', paddingTop: 5, borderTop: `1px solid ${C.borderSoft}`, marginBottom: 2 },
  adicRow: { display: 'grid', gridTemplateColumns: '1fr 90px 22px', gap: 6, alignItems: 'center', padding: '2px 0', borderBottom: `1px solid ${C.borderSoft}` },
  adicInp: { padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, background: C.surface, boxSizing: 'border-box', color: C.ink, width: '100%' },
  adicInpVal: { padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, background: C.surface, boxSizing: 'border-box', color: C.ink, width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  subtotalLbl: { padding: '4px 0 2px', fontWeight: 600, fontSize: 11.5, color: C.inkSoft, borderTop: `1px solid ${C.border}` },
  subtotalVal: { padding: '4px 0 2px', fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: `1px solid ${C.border}`, color: C.inkSoft, fontSize: 11.5 },
  pagadorTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6, padding: '5px 0 0', borderTop: `2px solid ${C.accent}`, color: C.ink, fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums' },
  pagadorTotalLbl: { fontSize: 11, color: C.inkSoft, fontWeight: 600 },

  // ── Bottom: Resumen + Registración Presea + Asientos (3 col, ~8cm c/u) ──
  bottomGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 300px)', gap: 10, alignItems: 'start' },
  tesoreriaBox: { background: 'transparent', border: 'none', padding: 0 },
  tesoBox: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', width: 'auto', fontFamily: 'inherit', fontSize: 12.5, display: 'flex', flexDirection: 'column' },
  tesoTit: { fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: C.inkMute, textTransform: 'uppercase', marginBottom: 6, textAlign: 'left' },
  tesoSub: { fontSize: 11, color: C.inkMute, marginBottom: 4 },
  tesoSep: { display: 'none' },
  tesoLin: { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderBottom: `1px solid ${C.borderSoft}`, fontVariantNumeric: 'tabular-nums' },
  tesoLinSm: { display: 'flex', justifyContent: 'space-between', gap: 4, padding: '1px 0', fontSize: 11 },
  tesoNameSm: { color: C.inkSoft, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 },
  tesoNumSm: { color: C.ink, fontSize: 11, fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' },
  tesoGroupTit: { fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 0 2px', paddingTop: 4, borderTop: `1px dashed ${C.borderSoft}` },
  tesoLinIn: { display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' },
  tesoNum: { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: C.ink },
  tesoTot: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0 0', marginTop: 6, borderTop: `2px solid ${C.accent}`, color: C.ink, fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums' },
  tesoLbl: { color: C.inkSoft, fontSize: 12 },
  tesoComp: { padding: '5px 0', borderBottom: `1px solid ${C.borderSoft}` },
  tesoCompInputs: { display: 'flex', gap: 5, alignItems: 'center', marginBottom: 2 },
  tesoInp: { padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', background: C.surface, color: C.ink },
  tesoLegLine: { color: C.inkSoft, fontSize: 12 },

  // ── Misceláneo ──────────────────────────────────────────
  resumen: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  resItem: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 10px', display: 'flex', alignItems: 'baseline', gap: 6 },
  resTotal: { background: C.accent, color: 'white', border: 'none' },
  resLbl: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.85 },
  resVal: { fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  chip: { display: 'inline-block', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', fontSize: 12, margin: 2 },
}
